/**
 * "Export project to TJR" (R44, decision 614; docs/specs/project-reporting/tjr-export.md): the project as a PitrexTJR
 * v59 job file (`Report_Log_<job>.tjf`), so the previous reporting program opens it while both are in use. Shown only
 * to Thomson Airborne (`tjrExportOffered`). This module builds the records, one string per record, in the writer's
 * order (TJF_IMPORT_EXPORT_SPEC.md §6); the core checks them against what the program's loader refuses and encodes
 * the file (`crates/teslyn-core/src/tjf/export.rs`).
 *
 * The rules that differ between Teslyn and the job file, each kept compatible as MB decided:
 * - Comp box and calibration hours (decision 162): Teslyn keeps them apart; the job file has the same two boxes
 *   (record 100 fields 15 and 16) and the previous program adds them up as its "Cal Hours" (`tjrCalHours`), so each is
 *   written to its own box and that program shows their sum.
 * - A fuel resupply (decision 159) records the new total, which sets the stock, and the amount delivered and a
 *   delivery docket: the job file's resupply row (026) holds the totals; the amount delivered and the docket stay in
 *   Teslyn.
 * - Fuel stock per store and location (decision 160): the job file has one stock per store, so every fuel record is
 *   written with each store's stock over all locations at that moment (`tjrFuelRows`).
 * - Two pilots on a flight: the job file has one pilot box; the first pilot is written and the export says so.
 *
 * Records Teslyn does not model (quote and contract data, JSA, JSP, block costs and terrain, accidents) are written
 * back as they were imported (`project.legacy_json.unmodelled`), in their places; a project that never came from a
 * job file leaves them out and the previous program uses its defaults.
 */
import { entryLineStatuses } from './tjr-adapter.js';
import { resolveCalEntries } from './cal-review.js';
import { qcLimits } from './calibration.js';
import {
  drumLitres,
  fuelStock,
  FUEL_STORES,
  FUEL_TYPES,
  ledgerValue,
  locationKey,
  refuelPlacement,
} from './fuel.js';
import type { FuelStore } from './fuel.js';
import { utmZoneOfEpsg } from './map-geo.js';
import { flownCreditM } from './progress.js';
import type {
  Aircraft,
  Block,
  CalKind,
  CalResult,
  DailyEntry,
  Flight,
  FuelLedgerEntry,
  FuelType,
  LineStatus,
  MaintenanceItem,
  Person,
  PlannedLine,
  Position,
  Project,
  ProjectCrew,
  Report,
} from './model.js';

/** The organisations "Export project to TJR" is shown to (decision 614: Thomson Airborne only). */
export const TJR_EXPORT_TENANT_SLUGS: readonly string[] = ['thomson-airborne'];

/** Whether the organisation sees "Export project to TJR". */
export function tjrExportOffered(tenantSlug: string | null | undefined): boolean {
  return tenantSlug != null && TJR_EXPORT_TENANT_SLUGS.includes(tenantSlug.trim().toLowerCase());
}

/** The job file version the previous reporting program opens (spec §2). */
export const TJF_EXPORT_VERSION = 59;

/** Everything the export reads, as the list commands return it. */
export interface TjrExportInput {
  readonly project: Project;
  readonly blocks: readonly Block[];
  readonly plan: readonly PlannedLine[];
  readonly flights: readonly Flight[];
  readonly statuses: readonly LineStatus[];
  readonly entries: readonly DailyEntry[];
  readonly cal_results: readonly CalResult[];
  readonly aircraft: readonly Aircraft[];
  /** The staff register, deleted people included (an entry keeps their name). */
  readonly people: readonly Person[];
  readonly project_crew?: readonly ProjectCrew[];
  readonly positions: readonly Position[];
  readonly fuel: readonly FuelLedgerEntry[];
  readonly maintenance: readonly MaintenanceItem[];
  /** The project's reports: the latest final report's total processed line km goes to record 673. */
  readonly reports?: readonly Report[];
}

export interface TjrExport {
  /** `Report_Log_F26048.tjf`. */
  readonly file_name: string;
  /** Code and body of each record, in the writer's order; the core encodes them. */
  readonly records: readonly string[];
  /** What the job file could not hold, or holds differently; shown after the export. */
  readonly warnings: readonly string[];
  readonly counts: {
    readonly daily_entries: number;
    readonly lines_flown: number;
    readonly planned_lines: number;
    readonly fuel_records: number;
  };
}

// ------------------------------------------------------------------------------------------- formatting (spec §1)

/** Delphi `FloatToStr`: the shortest form ("4.8", "0", "90"), never a negative zero. */
export function tjfFloat(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '0';
  const r = Math.round(v * 1e6) / 1e6;
  return Object.is(r, -0) || r === 0 ? '0' : String(r);
}

/** `FormatFloat('0.00')`. */
export function tjfFixed2(v: number | null | undefined): string {
  const n = v === null || v === undefined || !Number.isFinite(v) ? 0 : v;
  const s = n.toFixed(2);
  return s === '-0.00' ? '0.00' : s;
}

/** `IntToStr` of a rounded number. */
export function tjfInt(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '0';
  const r = Math.round(v);
  return r === 0 ? '0' : String(r);
}

/** Booleans are `-1` (true) and `0`. */
export const tjfBool = (b: boolean | null | undefined): string => (b ? '-1' : '0');

/** dd/mm/yyyy from YYYY-MM-DD. */
export function tjfDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** The 27 characters Windows-1252 places in 0x80 to 0x9F (the job file's code page). */
const CP1252_EXTRA = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ');
const TEXT_SWAP: Readonly<Record<string, string>> = { '~': '-', '|': '/', '^': '-' };

/**
 * Text as the job file can hold it (spec §1, §4 rule 7): no `~ | ^` (the separators; replaced by `- / -`), no line
 * breaks (a space), and Windows-1252 only (anything else becomes `?`). `changed` says whether anything was replaced.
 */
export function tjfText(s: string | null | undefined): { text: string; changed: boolean } {
  const src = s ?? '';
  let out = '';
  for (const ch of src.replace(/\r\n|\r|\n/g, ' ')) {
    const swap = TEXT_SWAP[ch];
    if (swap !== undefined) out += swap;
    else {
      const c = ch.codePointAt(0)!;
      out += c < 0x80 || (c >= 0xa0 && c <= 0xff) || CP1252_EXTRA.has(ch) ? ch : '?';
    }
  }
  return { text: out, changed: out !== src };
}

// ------------------------------------------------------------------------------------------- fuel (159, 160)

/** One fuel record as the job file holds it: totals per store and fuel type over every location. */
export interface TjrFuelRow {
  readonly kind: 'resupply' | 'dip';
  readonly date: string;
  readonly time: string;
  readonly location: string;
  /** Litres. */
  readonly truck_avgas_l: number;
  readonly truck_turbine_l: number;
  /** Drum counts. */
  readonly drums_avgas: number;
  readonly drums_turbine: number;
  /** True when other locations' stock was added to this record's own figures (decision 160). */
  readonly combined: boolean;
}

const stampOf = (date: string, time: string | null | undefined): string =>
  `${date} ${time != null && /^\d{2}:\d{2}$/.test(time) ? time : '00:00'}`;

/**
 * The fuel records for the job file (decisions 159 and 160). The job file keeps one stock per store (truck, drums)
 * and fuel type: its latest resupply or dip sets it and later truck or drum refuels draw it down. Teslyn keeps a stock
 * per location too, so each record is written with, for every store it sets, its own figure plus every other
 * location's stock at that moment (Teslyn's balance: its latest record less the refuels drawn there since). Then the
 * previous program's stock is the sum of the locations', as at every later record. With one location the records are
 * written as recorded. A resupply's total is written, as the program expects; the amount delivered is not in the file.
 */
export function tjrFuelRows(
  ledger: readonly FuelLedgerEntry[],
  entries: readonly DailyEntry[],
  aircraft: readonly Aircraft[],
  drumL: number,
): TjrFuelRow[] {
  const rows = ledger.filter((r) => r.deleted_at === null);
  const { names, placed } = refuelPlacement(rows, entries);
  // per location, the balance after each event, with the event's time and whether it was a fuel record
  const timelines = new Map<
    string,
    {
      at: string;
      record: boolean;
      id: string;
      balance: Readonly<Record<FuelStore, Readonly<Record<FuelType, number>>>>;
    }[]
  >();
  for (const key of names.keys()) {
    const own = rows.filter((r) => locationKey(r.location) === key);
    const stock = fuelStock(own, placed.get(key) ?? [], aircraft, drumL);
    timelines.set(
      key,
      stock.history.map((h) => ({
        at: stampOf(h.date, h.time),
        record: h.kind !== 'refuel',
        id: h.key,
        balance: h.balance,
      })),
    );
  }
  const balanceBefore = (key: string, at: string) => {
    let last: (typeof timelines extends Map<string, (infer T)[]> ? T : never) | undefined;
    for (const ev of timelines.get(key) ?? []) {
      // a record at the same minute comes before the refuels of that minute (the stock rule's order)
      if (ev.at < at || (ev.at === at && ev.record)) last = ev;
      else break;
    }
    return last?.balance;
  };
  const sorted = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const x = stampOf(a.r.date, a.r.time);
      const y = stampOf(b.r.date, b.r.time);
      return x !== y ? (x < y ? -1 : 1) : a.i - b.i;
    })
    .map((x) => x.r);
  return sorted.map((r) => {
    const own = locationKey(r.location);
    const at = stampOf(r.date, r.time);
    const value: Record<FuelStore, Record<FuelType, number>> = {
      truck: { avgas: 0, turbine: 0 },
      drums: { avgas: 0, turbine: 0 },
    };
    let combined = false;
    for (const store of FUEL_STORES) {
      const sets = FUEL_TYPES.some((f) => ledgerValue(r, store, f) > 0);
      for (const fuel of FUEL_TYPES) {
        let v = ledgerValue(r, store, fuel);
        if (sets) {
          for (const key of names.keys()) {
            if (key === own) continue;
            const b = balanceBefore(key, at)?.[store][fuel] ?? 0;
            if (b !== 0) combined = true;
            v += b;
          }
        }
        value[store][fuel] = v;
      }
    }
    return {
      kind: r.kind,
      date: r.date,
      time: r.time,
      location: r.location,
      truck_avgas_l: value.truck.avgas,
      truck_turbine_l: value.truck.turbine,
      drums_avgas: value.drums.avgas,
      drums_turbine: value.drums.turbine,
      combined,
    };
  });
}

// ------------------------------------------------------------------------------------------- the job file

const ENTRY_TYPE_CODE: Readonly<Record<DailyEntry['entry_type'], number>> = {
  flight: 1,
  standby: 2,
  maintenance: 3,
  suspended: 4,
};
const STANDBY_CODE: Readonly<Record<string, number>> = { diurnal: 0, weather: 1, pilot_rdo: 2, other: 3 };
const FUEL_SOURCE_CODE: Readonly<Record<string, number>> = { bowser: 0, drum: 1, truck: 2 };
const CAL_TYPE: Readonly<Record<CalKind, number>> = {
  th_sample_pre: 0,
  background_pre: 1,
  th_sample_post: 2,
  background_post: 3,
  test_801: 4,
  test_802: 5,
  th_peak: 6,
};
const CAL_VALUES: Readonly<Record<number, number>> = { 0: 7, 1: 7, 2: 7, 3: 7, 4: 4, 5: 4, 6: 2 };
/**
 * Decimals of each calibration value as the acquisition log prints them and the job file keeps them (F26048: counts
 * 11235.6, positions 559671.43 and 7224186.84, GPS height 418.6, Th peak 216.59 and FWHM 5.19).
 */
const CAL_DECIMALS: Readonly<Record<number, readonly number[]>> = {
  0: [1, 1, 1, 1, 2, 2, 1],
  1: [1, 1, 1, 1, 2, 2, 1],
  2: [1, 1, 1, 1, 2, 2, 1],
  3: [1, 1, 1, 1, 2, 2, 1],
  4: [1, 1, 1, 1],
  5: [1, 1, 1, 1],
  6: [2, 2],
};

/** The records after the line status and fuel dips, in the writer's order (spec §6); 668 and 673 are Teslyn's. */
const TAIL_CODES: readonly string[] = [
  '600',
  '610',
  '650',
  '651',
  '659',
  '660',
  '661',
  '662',
  '663',
  '664',
  '665',
  '666',
  '667',
  '668',
  '669',
  '670',
  '671',
  '672',
  '673',
  '674',
  '675',
  '676',
  '677',
  '712',
  '713',
  '714',
  '715',
  '716',
  '719',
  '720',
  '726',
  '727',
  '728',
  '730',
  '731',
  '732',
  '800',
  '801',
  '802',
  '803',
  '804',
  '805',
  '806',
  '807',
  '808',
  '809',
  '810',
  '811',
  '812',
  '813',
  '814',
  '815',
  '816',
  '817',
  '818',
  '819',
  '830',
];
/** Records written back as imported, before the daily entries. */
const HEAD_BAG_CODES = ['024', '027'];
/** Records Teslyn writes itself; the import's copies of them are not written back. */
const REGENERATED = new Set(['025', '033', '034', '036', '668', '673', '270', '271']);

type Bag = readonly (readonly [string, string])[];

function legacyOf(p: Project): Record<string, unknown> {
  return (p.legacy_json ?? {}) as Record<string, unknown>;
}

function bagOf(p: Project): Bag {
  const u = legacyOf(p).unmodelled;
  if (!Array.isArray(u)) return [];
  return u.filter(
    (x): x is [string, string] =>
      Array.isArray(x) && x.length === 2 && typeof x[0] === 'string' && typeof x[1] === 'string',
  );
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

const nameKey = (n: string): string => n.trim().replace(/\s+/g, ' ').toLowerCase();

/** A list record's rows (pilots, operators, aircraft, positions): keys in index order. */
class IndexList<T> {
  readonly rows: { key: string; item: T }[] = [];
  private readonly at = new Map<string, number>();
  add(key: string, item: T): number {
    const i = this.at.get(key);
    if (i !== undefined) return i;
    this.rows.push({ key, item });
    this.at.set(key, this.rows.length - 1);
    return this.rows.length - 1;
  }
  index(key: string | null | undefined): number {
    return key == null ? -1 : (this.at.get(key) ?? -1);
  }
}

/** The job number's digits and extension letter: F26048 → 26048 and "", F26048B → 26048 and "B". */
export function jobNumberParts(number: string): { digits: string; extension: string } {
  const m = /^[A-Za-z]?\s*(\d+)\s*([A-Za-z]?)$/.exec(number.trim());
  if (!m) return { digits: number.trim().replace(/\D/g, ''), extension: '' };
  return { digits: String(Number(m[1])), extension: m[2]!.toUpperCase() };
}

/** The total processed line km of the latest final report (TJR record 673), or null. */
export function latestProcessedKm(reports: readonly Report[] | undefined): number | null {
  const finals = (reports ?? [])
    .filter((r) => r.deleted_at === null && r.kind === 'final')
    .sort((a, b) => (a.generated_at < b.generated_at ? 1 : -1));
  for (const r of finals) {
    const km = (r.manifest_json as { final_report?: { processed_km?: unknown } }).final_report?.processed_km;
    if (typeof km === 'number' && Number.isFinite(km)) return km;
  }
  return null;
}

/** Build the job file's records from the project (see the module comment for what is kept how). */
export function tjrExportRecords(input: TjrExportInput): TjrExport {
  const live = <T extends { deleted_at: string | null }>(rows: readonly T[]): T[] =>
    rows.filter((r) => r.deleted_at === null);
  const p = input.project;
  const legacy = legacyOf(p);
  const bag = bagOf(p);
  const bagAll = (code: string): string[] => bag.filter(([c]) => c === code).map(([, raw]) => raw);
  const bagFirst = (code: string): string | undefined => bag.find(([c]) => c === code)?.[1];
  const lists = (legacy.tjf_lists ?? {}) as Record<string, unknown>;
  const warnings: string[] = [];
  const textChanged = new Set<string>();
  const text = (s: string | null | undefined, where: string): string => {
    const t = tjfText(s);
    if (t.changed) textChanged.add(where);
    return t.text;
  };
  const out: string[] = [];
  const rec = (code: string, body: string): void => {
    out.push(code + body);
  };
  const fields = (...parts: string[]): string => parts.map((x) => `${x}~`).join('');

  const entries = live(input.entries)
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.date.localeCompare(b.e.date) || a.i - b.i)
    .map((x) => x.e);
  const flights = live(input.flights);
  const peopleById = new Map(input.people.map((x) => [x.id, x]));
  const crew = live(input.project_crew ?? []);

  // ---- 010 pilots, 011 operators: the job's own order first (tjf_lists), then the project crew, then whoever flew
  type Crew = { name: string; rank: string };
  const pilots = new IndexList<Crew>();
  const operators = new IndexList<Crew>();
  const personName = (id: string | null | undefined): string | null => {
    if (!id) return null;
    const x = peopleById.get(id);
    return x ? x.name.trim().replace(/\s+/g, ' ') : null;
  };
  const crewOf = (role: 'pilot' | 'operator'): string[] =>
    crew
      .filter((c) => c.role === role)
      .map((c) => personName(c.person_id) ?? c.person_name ?? null)
      .filter((n): n is string => n !== null && n.trim() !== '');
  const slotName = (e: DailyEntry, slot: 'pilot' | 'operator1' | 'operator2' | 'pilot2'): string | null => {
    const id = e[`${slot}_id` as const];
    const n = personName(id) ?? e[`${slot}_name` as const] ?? null;
    return n && n.trim() !== '' ? n.trim().replace(/\s+/g, ' ') : null;
  };
  const rankOf = (name: string): string => {
    const x = input.people.find((q) => nameKey(q.name) === nameKey(name) && q.rank);
    if (x?.rank) return x.rank;
    const e = entries.find(
      (q) => slotName(q, 'pilot') !== null && nameKey(slotName(q, 'pilot')!) === nameKey(name),
    );
    return e?.pilot_rank ?? '';
  };
  const wantedPilots = new Set([
    ...crewOf('pilot').map(nameKey),
    ...entries
      .map((e) => slotName(e, 'pilot'))
      .filter((n): n is string => n !== null)
      .map(nameKey),
  ]);
  const wantedOperators = new Set([
    ...crewOf('operator').map(nameKey),
    ...entries
      .flatMap((e) => [slotName(e, 'operator1'), slotName(e, 'operator2')])
      .filter((n): n is string => n !== null)
      .map(nameKey),
  ]);
  for (const n of stringList(lists['010']))
    if (wantedPilots.has(nameKey(n))) pilots.add(nameKey(n), { name: n, rank: rankOf(n) });
  for (const n of crewOf('pilot')) pilots.add(nameKey(n), { name: n, rank: rankOf(n) });
  for (const e of entries) {
    const n = slotName(e, 'pilot');
    if (n) pilots.add(nameKey(n), { name: n, rank: rankOf(n) });
  }
  for (const n of stringList(lists['011']))
    if (wantedOperators.has(nameKey(n))) operators.add(nameKey(n), { name: n, rank: '' });
  for (const n of crewOf('operator')) operators.add(nameKey(n), { name: n, rank: '' });
  for (const e of entries)
    for (const slot of ['operator1', 'operator2'] as const) {
      const n = slotName(e, slot);
      if (n) operators.add(nameKey(n), { name: n, rank: '' });
    }
  const twoPilots = entries.filter((e) => slotName(e, 'pilot2') !== null).length;
  if (twoPilots > 0)
    warnings.push(
      `${twoPilots} ${twoPilots === 1 ? 'entry has' : 'entries have'} a second pilot: the previous reporting program has one pilot box, so only the first pilot is written.`,
    );

  // ---- 012 aircraft: the job's own, then those its entries, flights and maintenance items name
  const aircraft = new IndexList<Aircraft>();
  const usedIds = new Set<string>(
    [
      ...entries.map((e) => e.aircraft_id),
      ...flights.map((f) => f.aircraft_id),
      ...live(input.maintenance).map((m) => m.aircraft_id),
    ].filter((x): x is string => x !== null),
  );
  const byRego = (rego: string): Aircraft | undefined =>
    live(input.aircraft).find((a) => a.registration.trim().toUpperCase() === rego.trim().toUpperCase());
  for (const rego of stringList(lists['012'])) {
    const a = byRego(rego);
    if (a) aircraft.add(a.id, a);
  }
  for (const a of input.aircraft) if (usedIds.has(a.id)) aircraft.add(a.id, a);

  // ---- 014 to 016 positions
  const positionLists = {
    base_mag: new IndexList<Position>(),
    ground_cal: new IndexList<Position>(),
    test_line: new IndexList<Position>(),
  };
  const positions = live(input.positions);
  for (const [code, kind] of [
    ['014', 'base_mag'],
    ['015', 'ground_cal'],
    ['016', 'test_line'],
  ] as const) {
    const list = positionLists[kind];
    for (const n of stringList(lists[code])) {
      const pos = positions.find((x) => x.kind === kind && nameKey(x.name) === nameKey(n));
      if (pos) list.add(pos.id, pos);
    }
    for (const pos of positions) if (pos.kind === kind) list.add(pos.id, pos);
  }

  // ---- blocks: slot = digit − 1 (TJR's nine blocks; block 1 is TJR's block 0)
  const slots: (Block | null)[] = Array.from({ length: 9 }, () => null);
  for (const b of live(input.blocks)) {
    const d = Number(b.digit);
    if (!Number.isInteger(d) || d < 1 || d > 9) {
      warnings.push(
        `Block ${b.name} (digit ${b.digit}) has no place among the nine blocks of the job file: left out.`,
      );
      continue;
    }
    if (slots[d - 1]) {
      warnings.push(
        `Blocks ${slots[d - 1]!.name} and ${b.name} share digit ${b.digit}: only ${slots[d - 1]!.name} is written.`,
      );
      continue;
    }
    slots[d - 1] = b;
  }
  const slotOf = new Map<string, number>();
  slots.forEach((b, i) => {
    if (b) slotOf.set(b.id, i);
  });
  const legacy013 = stringList(legacy.block_records_013).map((raw) => raw.split('~'));
  const legacy271 = (legacy.boundary_records_271 ?? {}) as Record<string, unknown>;
  // the import's block groups (273 to 279) belong to its named blocks in slot order
  const originalNamed = legacy013
    .map((f, i) => ({ i, named: (f[0] ?? '').trim() !== '' }))
    .filter((x) => x.named)
    .map((x) => x.i);
  const groupOfSlot = new Map<number, number>(originalNamed.map((slot, k) => [slot, k]));

  // ---- header
  rec('003', '-1');
  rec('005', String(TJF_EXPORT_VERSION));
  rec('008', p.job_type === 'helicopter' ? '1' : '0');
  for (const [i, r] of pilots.rows.entries())
    rec('010', fields(String(i), text(r.item.name, 'crew names'), text(r.item.rank, 'crew names')));
  for (const [i, r] of operators.rows.entries())
    rec('011', fields(String(i), text(r.item.name, 'crew names')));

  // aircraft records (21 fields, spec §3.2.1)
  for (const [i, { item: a }] of aircraft.rows.entries()) {
    const lj = (a.legacy_json ?? {}) as Record<string, unknown>;
    const l = (k: string): string =>
      typeof lj[k] === 'string' || typeof lj[k] === 'number' ? String(lj[k]) : '';
    const [typeBrand, ...typeModel] = a.type_name.trim().split(/\s+/);
    const brand = l('brand') || (typeBrand ?? '');
    const model = l('model') || typeModel.join(' ');
    const renewals = entries
      .map((e, idx) => ({ e, idx }))
      .filter(({ e }) => e.aircraft_id === a.id && e.new_mr_ttis_h !== null);
    const fuelType = a.fuel_type === 'avgas' ? '0' : a.fuel_type === 'turbine' ? '1' : l('fuel_type') || '0';
    const engines = a.engines ?? (l('engines') ? Number(l('engines')) : 1);
    const wing =
      a.airframe === 'heli' ? '1' : a.airframe ? '0' : l('wing') || (p.job_type === 'helicopter' ? '1' : '0');
    const tracks = a.engine_starts_tracked ?? (l('track_cycles') ? l('track_cycles') === '-1' : false);
    rec(
      '012',
      fields(
        String(i),
        text(a.registration.trim().toUpperCase(), 'aircraft'),
        a.fuel_flow_lph != null ? tjfInt(a.fuel_flow_lph) : l('fuel_flow_lph') || '0',
        a.mr_interval_h != null ? tjfInt(a.mr_interval_h) : l('mr_interval_hrs') || '0',
        a.ttis_at_issue_h != null ? tjfFloat(a.ttis_at_issue_h) : l('ttis_at_issue'),
        renewals.map(({ idx }) => `${idx} `).join(''),
        a.ttis_at_start_h != null ? tjfFloat(a.ttis_at_start_h) : l('ttis_at_start'),
        a.max_flight_h != null ? tjfFloat(a.max_flight_h) : l('max_flight_hrs') || '0',
        fuelType,
        text(brand, 'aircraft'),
        text(model, 'aircraft'),
        renewals.map(({ e }) => `${(e.new_mr_ttis_h ?? 0).toFixed(1)} `).join(''),
        tjfBool(tracks),
        String(engines),
        l('cruise_kts') || '0',
        l('stall_kts') || '0',
        text(l('engine_type'), 'aircraft'),
        wing,
        l('corner_image_id'),
        l('image_id'),
        l('jsa_data'),
      ),
    );
  }

  // nine block records
  for (let s = 0; s < 9; s++) {
    const b = slots[s];
    const old = legacy013[s] ?? [];
    const o = (i: number, dflt: string): string => (old[i] ?? '').trim() || dflt;
    if (!b) {
      rec('013', fields('', '0', 'S', '0', '100', '0', '0', '90', '0', '0', '0'));
      continue;
    }
    const z = utmZoneOfEpsg(b.epsg ?? p.epsg);
    const num = (v: number | null, i: number, dflt: string, f: (n: number) => string): string =>
      v != null ? f(v) : o(i, dflt);
    rec(
      '013',
      fields(
        text(b.name, 'block names'),
        z ? String(z.zone) : o(1, '0'),
        z ? z.hemisphere : o(2, 'S'),
        num(b.terrain_clearance_m, 3, '0', tjfInt),
        num(b.trav_spacing_m, 4, '100', tjfInt),
        num(b.trav_direction_deg, 5, '0', tjfFloat),
        num(b.tie_spacing_m, 6, '0', tjfInt),
        num(b.tie_direction_deg, 7, '90', tjfFloat),
        o(8, '0'),
        o(9, '0'),
        o(10, '0'),
      ),
    );
    if (!z && !(old[1] ?? '').trim())
      warnings.push(
        `Block ${b.name} has no UTM zone: written as zone 0, which the previous reporting program cannot place.`,
      );
  }

  for (const [code, kind] of [
    ['014', 'base_mag'],
    ['015', 'ground_cal'],
    ['016', 'test_line'],
  ] as const)
    for (const [i, r] of positionLists[kind].rows.entries())
      rec(code, fields(String(i), text(r.item.name, 'positions')));

  rec('017', fields(text(p.operating_base || p.basemag_name, 'project details')));
  rec('020', text(p.area || p.title, 'project details'));
  const job = jobNumberParts(p.number);
  rec('021', job.digits || '0');
  rec('022', text(p.client_name, 'project details'));
  rec('023', job.extension);
  const head = (code: string, dflt: string): void => rec(code, bagFirst(code) ?? dflt);
  head(HEAD_BAG_CODES[0]!, '0');
  // 025 "Flag if TTIS rem <=": 0 Ignore, 1 to 4 = 5 to 20 h (Teslyn's MR warning, null is 20 h)
  const warnH = p.mr_warn_h ?? 20;
  const ttisIdx = [0, 5, 10, 15, 20].indexOf(warnH);
  rec('025', ttisIdx >= 0 ? String(ttisIdx) : (bagFirst('025') ?? '4'));
  if (ttisIdx < 0)
    warnings.push(
      `The MR warning of ${warnH} h has no match in the previous reporting program (0, 5, 10, 15 or 20 h).`,
    );
  head(HEAD_BAG_CODES[1]!, '0');

  // 026 resupplies (decisions 159, 160)
  const fuelRows = tjrFuelRows(live(input.fuel), entries, input.aircraft, drumLitres(p));
  const fuelRec = (r: TjrFuelRow): string => {
    const drums = (n: number): string => {
      if (Math.abs(n - Math.round(n)) > 1e-6)
        warnings.push(
          `A fuel record of ${tjfDate(r.date)} holds ${Math.round(n * 10) / 10} drums: written as ${Math.round(n)} (the job file counts whole drums).`,
        );
      return tjfInt(n);
    };
    return fields(
      `${tjfDate(r.date)} ${/^\d{2}:\d{2}$/.test(r.time) ? r.time : '00:00'} `,
      tjfInt(r.truck_avgas_l),
      tjfInt(r.truck_turbine_l),
      drums(r.drums_avgas),
      drums(r.drums_turbine),
      r.kind === 'dip' ? '' : text(r.location, 'fuel locations'),
    );
  };
  for (const r of fuelRows) if (r.kind === 'resupply') rec('026', fuelRec(r));
  if (fuelRows.some((r) => r.combined))
    warnings.push(
      'Fuel is recorded at more than one location: each fuel record holds the stock of every location at that time, as the previous reporting program keeps one stock per store.',
    );
  const uplifts = live(input.fuel).filter(
    (r) =>
      r.kind === 'resupply' &&
      ([r.uplift_truck_avgas_l, r.uplift_truck_turbine_l, r.uplift_drums_avgas, r.uplift_drums_turbine].some(
        (v) => v != null,
      ) ||
        r.docket_path),
  ).length;
  if (uplifts > 0)
    warnings.push(
      `${uplifts} resupply ${uplifts === 1 ? 'record holds' : 'records hold'} the amount delivered or a delivery docket: the job file keeps the new total only; both stay in Teslyn.`,
    );

  // 033 / 034 / 036: the thorium and position limits (Teslyn's QC limits)
  const limits = qcLimits(p);
  const limitIdx = (v: number, max: number): string => String(Math.min(max, Math.max(0, Math.round(v))));
  rec('033', limitIdx(limits.handSamplePct, 10));
  rec('034', limitIdx(limits.positionM, 20));
  rec('036', limitIdx(limits.testLinePct, 10));

  // ---- daily entries (100), with their line status (400) and calibration rows (field 30)
  const ofEntry = entryLineStatuses(entries, live(input.statuses), flights);
  const planById = new Map(input.plan.map((l) => [l.id, l]));
  const cal = resolveCalEntries(
    live(input.cal_results).filter((r) => (r.review_state ?? 'confirmed') === 'confirmed'),
    entries,
    flights,
  );
  const calOf = new Map<string, CalResult[]>();
  for (const r of cal) {
    if (!r.daily_entry_id) continue;
    calOf.set(r.daily_entry_id, [...(calOf.get(r.daily_entry_id) ?? []), r]);
  }
  let skippedCal = 0;
  const calData = (e: DailyEntry): string =>
    (calOf.get(e.id) ?? [])
      .map((r) => {
        const type = CAL_TYPE[r.kind];
        const want = CAL_VALUES[type]!;
        const vj = r.values_json as { type?: unknown; values?: unknown };
        let vals: (number | null)[] =
          vj?.type === type &&
          Array.isArray(vj.values) &&
          vj.values.length === want &&
          vj.values.every((v) => typeof v === 'number')
            ? (vj.values as number[])
            : type === 6
              ? [r.th_peak, r.fwhm_pct]
              : type >= 4
                ? [r.tc, r.k, r.u, r.th]
                : [r.tc, r.k, r.u, r.th, r.easting, r.northing, r.altitude_m];
        if (vals.some((v) => v === null || !Number.isFinite(v))) {
          skippedCal += 1;
          return '';
        }
        vals = vals as number[];
        const places = CAL_DECIMALS[type]!;
        return `${tjfBool(r.used)},${type},${vals.map((v, i) => (v as number).toFixed(places[i] ?? 2)).join(',')},|`;
      })
      .join('');

  const lineRows: string[] = [];
  let linesFlown = 0;
  for (const [idx, e] of entries.entries()) {
    const own = ofEntry.get(e.id) ?? [];
    let flownM = 0;
    let acceptedM = 0;
    for (const s of own) {
      const pl = planById.get(s.planned_line_id);
      const dir =
        s.direction_deg ??
        (pl ? Math.round(((Math.atan2(pl.x2 - pl.x1, pl.y2 - pl.y1) * 180) / Math.PI + 360) % 360) : 0);
      const flown = flownCreditM(s);
      const accepted = s.acceptance === 'pending' ? 0 : s.accepted_m;
      flownM += flown;
      acceptedM += accepted;
      lineRows.push(
        '400' +
          fields(
            '-1',
            String(idx),
            String(s.line_number),
            tjfInt(dir),
            tjfBool(s.use_km),
            tjfFixed2(flown),
            tjfFixed2(accepted),
            text(s.reason, 'refly reasons').slice(0, 100),
            text(s.note, 'refly reasons').slice(0, 100),
          ),
      );
    }
    linesFlown += own.length;
    const blockTokens = e.blocks
      .map((id) => slotOf.get(id))
      .filter((s): s is number => s !== undefined)
      .map((s) => `${s} `)
      .join('');
    const defSlot = e.default_block_id ? (slotOf.get(e.default_block_id) ?? -1) : -1;
    const pilotName = slotName(e, 'pilot');
    const op1 = slotName(e, 'operator1');
    const op2 = slotName(e, 'operator2');
    rec(
      '100',
      fields(
        String(idx),
        tjfDate(e.date),
        String(positionLists.base_mag.index(e.base_mag_position_id)),
        String(positionLists.ground_cal.index(e.ground_cal_position_id)),
        String(positionLists.test_line.index(e.test_line_position_id)),
        blockTokens,
        String(defSlot),
        String(aircraft.index(e.aircraft_id)),
        String(op1 ? operators.index(nameKey(op1)) : -1),
        String(op2 ? operators.index(nameKey(op2)) : -1),
        String(pilotName ? pilots.index(nameKey(pilotName)) : -1),
        String(e.flt_num ?? 0),
        String(ENTRY_TYPE_CODE[e.entry_type]),
        tjfFloat(e.days_alloc),
        tjfFloat(e.svy_hours),
        // decision 162: comp box and calibration hours in their own boxes; the previous program adds them up as its
        // "Cal Hours" (tjrCalHours)
        tjfFloat(e.cbx_hours),
        tjfFloat(e.cal_hours),
        tjfFloat(e.fry_hours),
        tjfFloat(e.ext_hours),
        tjfInt(e.fuel_used_l),
        String(e.fuel_source ? (FUEL_SOURCE_CODE[e.fuel_source] ?? -1) : -1),
        e.takeoff_time ?? '',
        tjfFixed2(flownM),
        tjfFixed2(acceptedM),
        String(own.length),
        tjfInt(e.starts),
        ((e.oil_l ?? 0) + (e.oil_r ?? 0)).toFixed(1),
        '0',
        text(e.op_comment, 'comments'),
        text(e.qc_comment, 'comments'),
        calData(e),
        String(e.entry_type === 'standby' && e.standby_type ? (STANDBY_CODE[e.standby_type] ?? -1) : -1),
        text(e.fuel_location, 'fuel locations'),
        tjfInt(e.fuel_load_l),
        text(e.log_file ?? 'None', 'log file names') || 'None',
        tjfBool(e.mobilization),
      ),
    );
  }
  if (skippedCal > 0)
    warnings.push(
      `${skippedCal} calibration ${skippedCal === 1 ? 'result has' : 'results have'} a missing value and ${skippedCal === 1 ? 'is' : 'are'} not written.`,
    );
  const withEntry = new Set([...ofEntry.values()].flat().map((s) => s.id));
  const loose = live(input.statuses).filter((s) => !withEntry.has(s.id));
  const looseMob = loose.filter((s) => {
    const f = flights.find((x) => x.id === s.flight_id);
    return f && entries.some((e) => e.mobilization && e.date === f.date && e.flt_num === f.flt_num);
  }).length;
  if (loose.length - looseMob > 0)
    warnings.push(
      `${loose.length - looseMob} flown ${loose.length - looseMob === 1 ? 'line belongs' : 'lines belong'} to flights with no daily entry: the job file keeps lines on daily entries only, so they are not written.`,
    );

  // ---- line geometry 250 to 258: the planned lines (no reflights or patches; they are line status rows)
  let planned = 0;
  let negative = 0;
  for (let s = 0; s < 9; s++) {
    const b = slots[s];
    if (!b) continue;
    for (const l of input.plan) {
      if (
        l.deleted_at !== null ||
        l.block_id !== b.id ||
        l.reflight_of_line_id !== null ||
        l.segment_from_m != null
      )
        continue;
      if ([l.x1, l.y1, l.x2, l.y2, l.number].some((v) => !Number.isFinite(v) || v < 0)) {
        negative += 1;
        continue;
      }
      planned += 1;
      rec(
        String(250 + s),
        `${l.number} ${tjfFixed2(l.x1)} ${tjfFixed2(l.y1)} ${tjfFixed2(l.x2)} ${tjfFixed2(l.y2)} ${l.kind === 'tie' ? 4 : 1} `,
      );
    }
  }
  if (negative > 0)
    warnings.push(
      `${negative} planned ${negative === 1 ? 'line has' : 'lines have'} a negative coordinate, which the previous reporting program cannot read: left out.`,
    );

  // ---- per block: 270 marker, boundary 271, then the block's records as imported (273, 275 to 277, 279)
  const groupRecords = (k: number | undefined): Record<string, string | undefined> => {
    if (k === undefined) return {};
    const pick = (code: string): string | undefined => bagAll(code)[k];
    return {
      '273': pick('273'),
      '275': pick('275'),
      '276': pick('276'),
      '277': pick('277'),
      '279': pick('279'),
    };
  };
  for (let s = 0; s < 9; s++) {
    const b = slots[s];
    if (!b) continue;
    rec('270', String(s));
    const raw = stringList(legacy271[String(s)]);
    const firstPoly = (() => {
      const pts: [number, number][] = [];
      let polys = 0;
      for (const r of raw) {
        if (r.startsWith('|')) continue;
        if (r.startsWith('~')) {
          polys += 1;
          if (polys > 1) break;
          continue;
        }
        const [e, n] = r.split('~').map(Number);
        if (polys === 1 && Number.isFinite(e) && Number.isFinite(n)) pts.push([e!, n!]);
      }
      return pts;
    })();
    const same =
      raw.length > 0 &&
      (b.boundary === null ||
        (b.boundary.length === firstPoly.length &&
          b.boundary.every(
            ([e, n], i) => Math.abs(e - firstPoly[i]![0]) < 0.006 && Math.abs(n - firstPoly[i]![1]) < 0.006,
          )));
    if (same) for (const r of raw) rec('271', r);
    else if (b.boundary && b.boundary.length > 0) {
      rec('271', '|1');
      rec('271', `~${b.boundary.length}`);
      for (const [e, n] of b.boundary) rec('271', fields(tjfFixed2(e), tjfFixed2(n)));
      if (raw.filter((r) => r.startsWith('~')).length > 1)
        warnings.push(
          `Block ${b.name}: its boundary changed in Teslyn, so only its outline is written (the excluded areas of the job file are left out).`,
        );
    }
    const g = groupRecords(groupOfSlot.get(s));
    rec('273', g['273'] ?? '');
    rec('275', g['275'] ?? '0.00');
    rec('276', g['276'] ?? '0');
    rec('277', g['277'] ?? '0');
    if (g['279'] !== undefined) rec('279', g['279']);
  }
  for (let s = 0; s < 9; s++) if (!slots[s]) rec('270', String(s));

  // ---- 297, 300, 302 as imported; 350 maintenance items; 400 line status; 500 dips
  for (const code of ['297', '300', '302']) for (const raw of bagAll(code)) rec(code, raw);
  for (const m of live(input.maintenance)) {
    const ai = aircraft.index(m.aircraft_id);
    if (ai < 0) continue;
    rec('350', fields(String(ai), tjfDate(m.date), text(m.text, 'maintenance items'), tjfBool(m.fixed)));
  }
  out.push(...lineRows);
  for (const r of fuelRows) if (r.kind === 'dip') rec('500', fuelRec(r));

  // ---- the rest as imported, with the state (668) and the total processed line km (673) from Teslyn
  const processedKm = latestProcessedKm(input.reports);
  for (const code of TAIL_CODES) {
    if (code === '668') {
      if (p.state.trim() !== '' || bagFirst('668') !== undefined)
        rec('668', text(p.state, 'project details'));
      continue;
    }
    if (code === '673') {
      if (processedKm !== null) rec('673', tjfFloat(processedKm));
      else {
        const old = bagFirst('673');
        if (old !== undefined) rec('673', old);
      }
      continue;
    }
    for (const raw of bagAll(code)) rec(code, raw);
  }
  // anything else the import kept, in its order (the previous program ignores codes it does not know)
  const placedCodes = new Set([
    ...HEAD_BAG_CODES,
    ...TAIL_CODES,
    '273',
    '275',
    '276',
    '277',
    '279',
    '297',
    '300',
    '302',
  ]);
  for (const [code, raw] of bag) if (!placedCodes.has(code) && !REGENERATED.has(code)) rec(code, raw);

  if (textChanged.size > 0)
    warnings.push(
      `Characters the job file cannot hold (~ | ^, line breaks, or letters outside Western European) were replaced in: ${[...textChanged].sort().join(', ')}.`,
    );

  return {
    file_name: `Report_Log_${p.number.trim().replace(/[^A-Za-z0-9_-]/g, '')}.tjf`,
    records: out,
    warnings,
    counts: {
      daily_entries: entries.length,
      lines_flown: linesFlown,
      planned_lines: planned,
      fuel_records: fuelRows.length,
    },
  };
}
